import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import productsData from './data/products.json';
import './styles/search.css';

const SearchComponent = ({ onClose }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const searchRef = useRef(null);
  const resultsRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsDropdownVisible(false);
        setIsFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (event) => {
    const term = event.target.value;
    setSearchTerm(term);
    
    if (term.trim()) {
      const searchTerms = term.toLowerCase().split(' ').filter(t => t.length > 0);
      
      const filtered = productsData.products
        .map(product => {
          const nameLower = product.name.toLowerCase();
          const categoryLower = product.category.toLowerCase();
          
          let score = 0;
          searchTerms.forEach(searchTerm => {
            if (nameLower.includes(searchTerm)) score += 2;
            if (categoryLower.includes(searchTerm)) score += 1;
          });
          
          return { ...product, score };
        })
        .filter(product => product.score > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, 6);
      
      setSearchResults(filtered);
      setIsDropdownVisible(true);
    } else {
      setSearchResults([]);
      setIsDropdownVisible(false);
    }
  };

  const highlightMatch = (text, term) => {
    if (!term) return text;
    const searchTerms = term.toLowerCase().split(' ').filter(t => t.length > 0);
    
    return searchTerms.reduce((highlightedText, searchTerm) => {
      const regex = new RegExp(`(${searchTerm})`, 'gi');
      return highlightedText.split(regex).map((part, index) => 
        part.toLowerCase() === searchTerm.toLowerCase() ? 
          <span key={index} className="highlight">{part}</span> : part
      );
    }, text);
  };

  const handleProductClick = (product) => {
    navigate(`/product/${product.id}`);
    setSearchTerm('');
    setIsDropdownVisible(false);
    if (onClose) onClose();
  };

  const clearSearch = () => {
    setSearchTerm('');
    setSearchResults([]);
    setIsDropdownVisible(false);
  };

  return (
    <div className="search-container" ref={searchRef}>
      <div className={`search-bar ${isFocused ? 'focused' : ''}`}>
        <Search className="search-icon" />
        <input
          type="text"
          className="search-input"
          placeholder="Search products..."
          value={searchTerm}
          onChange={handleSearch}
          onKeyDown={(e) => e.key === 'Escape' && clearSearch()}
          onFocus={() => {
            setIsFocused(true);
            if (searchTerm) setIsDropdownVisible(true);
          }}
          aria-label="Search products"
        />
        {searchTerm && (
          <button
            className="clear-button"
            onClick={clearSearch}
            aria-label="Clear search"
          >
            <X />
          </button>
        )}
      </div>
      
      {isDropdownVisible && (
        <div className="search-results" ref={resultsRef}>
          {searchResults.length > 0 ? (
            <>
              <div className="results-header">
                {searchResults.length} {searchResults.length === 1 ? 'result' : 'results'} found
              </div>
              <div className="results-container">
                {searchResults.map((product) => (
                  <button
                    key={product.id}
                    className="search-result-item"
                    onClick={() => handleProductClick(product)}
                  >
                    <div className="product-image-container">
                      <img 
                        src={product.image} 
                        alt=""
                        className="product-image"
                        loading="lazy"
                      />
                    </div>
                    <div className="product-info">
                      <div className="product-name">
                        {highlightMatch(product.name, searchTerm)}
                      </div>
                      <div className="product-category">
                        {highlightMatch(product.category, searchTerm)}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <div className="no-results">
              <p className="no-results-message">No products found for "{searchTerm}"</p>
              <p className="no-results-suggestion">
                Try different keywords or check the spelling
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchComponent;